// src/store/auth-store.ts
import { create } from 'zustand';
import { toast } from 'sonner';
import * as api from '../api/page';
import type { CurrentUser, LoginCredentials, RegisterPayload } from '../types/index';
import { useOrganizationStore } from './organization-store';

interface AuthState {
    token: string | null;
    user: CurrentUser | null;
    isAuthenticating: boolean;
    isLoading: boolean;
    isUpdating: boolean;
    error: string | null;
    login: (credentials: LoginCredentials) => Promise<boolean>;
    register: (payload: RegisterPayload) => Promise<boolean>;
    logout: () => Promise<void>;
    checkAuthStatus: () => Promise<void>;
    updateProfile: (data: { full_name: string }) => Promise<boolean>;
}

let authCheckCompleted = false;

export const useAuthStore = create<AuthState>((set, get) => ({
    token: localStorage.getItem("authToken"),
    user: null,
    isAuthenticating: true,
    isLoading: false,
    isUpdating: false,
    error: null,

    login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
            const { access_token } = await api.login(credentials);
            localStorage.setItem("authToken", access_token);
            set({ token: access_token });
            authCheckCompleted = false;
            await get().checkAuthStatus();
            return true;
        } catch (err: any) {
            set({ error: err.message, isLoading: false });
            return false;
        } finally {
            set({ isLoading: false });
        }
    },

    register: async (payload) => {
        set({ isLoading: true, error: null });
        try {
            // The API call to register a new user
            await api.register(payload);
            
            // FIX: Removed the sendVerificationEmail call
            // await api.sendVerificationEmail(registeredUser.email);
            
            // FIX: Updated the success toast message
            toast.success("Registration Successful!", { description: "Your account has been created." });
            return true;
        } catch (err: any) {
            set({ error: err.message, isLoading: false });
            return false;
        } finally {
            set({ isLoading: false });
        }
    },

    checkAuthStatus: async () => {
        if (authCheckCompleted) {
            set({ isAuthenticating: false });
            return;
        }
        
        const token = get().token;
        if (token) {
            try {
                const userData = await api.getCurrentUser();
                set({ user: userData, error: null });
                if (userData.organization_id) {
                    useOrganizationStore.getState().fetchUserPermissions();
                }
            } catch (err) {
                get().logout();
            }
        }
        set({ isAuthenticating: false });
        authCheckCompleted = true;
    },

    logout: async () => {
        try {
            if (get().token) await api.logout();
        } catch (err) {
            console.error("Logout API failed, continuing local logout.", err);
        } finally {
            localStorage.removeItem("authToken");
            set({ token: null, user: null, isAuthenticating: false });
            useOrganizationStore.getState().clearOrganization();
            authCheckCompleted = false;
        }
    },

    updateProfile: async (data: { full_name: string }) => {
        set({ isUpdating: true });
        try {
            const updatedUser = await api.updateMe(data);
            set({ user: updatedUser });
            toast.success("Profile updated successfully!");
            return true;
        } catch (err: any) {
            toast.error("Failed to update profile", { description: err.message });
            return false;
        } finally {
            set({ isUpdating: false });
        }
    },
}));